/* One page scroll, with a chapter guide that follows the reader. Native anchors
   and the full article remain available when JavaScript is unavailable. */
if (!customElements.get('collection-reading')) {
  customElements.define('collection-reading', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.guide = this.querySelector('.collection-reading__guide');
      this.body = this.querySelector('.collection-reading__body');
      if (!this.guide || !this.body) return;
      this.links = [...this.guide.querySelectorAll('a[href^="#"]')];
      this.headings = this.links.map(link => document.getElementById(link.hash.slice(1)));
      if (this.headings.some(heading => !heading || !this.body.contains(heading))) return;

      this.list = this.guide.querySelector('ol');
      this.header = document.querySelector('.section-header');
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.desktop = matchMedia('(min-width: 990px)');
      this.active = null;
      this.counter = document.createElement('span');
      this.counter.className = 'collection-reading__counter';
      this.guide.querySelector('.collection-reading__label').append(this.counter);
      this.picker = document.createElement('select');
      this.picker.className = 'collection-reading__picker';
      this.picker.setAttribute('aria-label', 'Gå til afsnit');
      this.picker.add(new Option('Gå til afsnit…', ''));
      this.links.forEach((link, index) => this.picker.add(new Option(`${index + 1}. ${link.textContent.trim()}`, String(index))));
      this.list.before(this.picker);
      this.picker.addEventListener('change', () => {
        if (this.picker.value !== '') this.links[Number(this.picker.value)].click();
        this.schedule();
      }, { signal });
      // Focusing a link reveals it inside the guide, never moving the article.
      this.guide.addEventListener('focusin', event => {
        if (this.links.includes(event.target)) this.reveal(event.target);
      }, { signal });
      this.guide.addEventListener('focusout', () => this.schedule(), { signal });
      this.guide.addEventListener('click', event => {
        if (event.target.closest('a[href^="#"]')) {
          // The theme otherwise reveals its header over an upward anchor jump.
          this.header?.querySelector('sticky-header')?.dispatchEvent(new Event('preventHeaderReveal'));
        }
      }, { signal });
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      window.addEventListener('hashchange', this.schedule, { signal });
      window.addEventListener('pageshow', this.schedule, { signal });
      this.observer = new ResizeObserver(this.schedule);
      this.observer.observe(this.body);
      this.observer.observe(this.guide);
      if (this.header) {
        this.observer.observe(this.header);
        this.headerObserver = new MutationObserver(this.schedule);
        this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class'] });
        this.header.addEventListener('transitionend', this.schedule, { signal });
      }
      this.setAttribute('data-reading-ready', '');
      this.schedule();
      document.fonts?.ready.then(() => { if (this.controller) this.schedule(); });
    }

    update() {
      const headerRect = this.header?.getBoundingClientRect();
      const pinnedHeader = this.header?.classList.contains('shopify-section-header-sticky');
      const hiddenHeader = this.header?.classList.contains('shopify-section-header-hidden') && !this.header?.classList.contains('menu-open');
      // Reserve the final header height during its reveal animation, so the
      // reading guide cannot be covered when the reader stops scrolling.
      const headerBottom = pinnedHeader ? (hiddenHeader ? Math.max(0, headerRect.bottom) : this.header.offsetHeight) : (headerRect?.top <= 1 ? headerRect.bottom : 0);
      const top = Math.max(16, headerBottom + 16);
      this.style.setProperty('--reading-top', `${top}px`);
      const readingLine = top + (this.desktop.matches ? 16 : this.guide.offsetHeight + 16);
      this.style.setProperty('--reading-anchor-offset', `${readingLine}px`);
      // Mark the chapter entering the upper reading area, not an exact pixel.
      // Keep a revealed header's space in this threshold: a native anchor can
      // land with that offset just before the header slides out of view.
      const headerAllowance = Math.max(0, (this.header?.offsetHeight || 0) + 16 - top);
      const activationLine = Math.max(readingLine + headerAllowance + 24, innerHeight * 0.2);
      const bodyRect = this.body.getBoundingClientRect();
      let index = -1;
      if (bodyRect.top <= innerHeight && bodyRect.bottom > top) {
        index = 0;
        this.headings.forEach((heading, position) => {
          if (heading.getBoundingClientRect().top <= activationLine) index = position;
        });
        // A short final chapter can reach the end of the viewport without its
        // heading reaching the reading line. Don't leave the penultimate active.
        if (bodyRect.bottom <= innerHeight && bodyRect.top < readingLine) index = this.headings.length - 1;
      }
      if (index !== this.active) {
        this.active = index;
        this.links.forEach((link, position) => {
          if (position === index) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        this.counter.textContent = index < 0 ? ` · ${this.links.length} afsnit` : ` · ${index + 1} af ${this.links.length}`;
      }
      // Mobile uses one compact sticky picker; desktop also shows the guide.
      if (document.activeElement !== this.picker) this.picker.value = this.desktop.matches || index < 0 ? '' : String(index);
      if (this.desktop.matches && index >= 0 && !this.links.includes(document.activeElement)) this.reveal(this.links[index]);
    }

    reveal(link) {
      const viewport = this.list.getBoundingClientRect();
      const item = link.getBoundingClientRect();
      const padding = 8;
      if (item.top < viewport.top + padding) this.list.scrollTop += item.top - viewport.top - padding;
      else if (item.bottom > viewport.bottom - padding) this.list.scrollTop += item.bottom - viewport.bottom + padding;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.headerObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.counter?.remove();
      this.picker?.remove();
      this.links?.forEach(link => link.removeAttribute('aria-current'));
      this.removeAttribute('data-reading-ready');
    }
  });
}
